function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable react/no-multi-comp */
import Fade from "@mui/material/Fade";
import { useDeepCompareMemo } from "@react-hookz/web";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SwitchTransition } from "react-transition-group";
import { useLatest } from "react-use";
import { ResetAfterSuccessTimeout } from "./consts";
import { NewsletterContextProvider } from "./contexts";
import { DisplayComponent, Variant } from "./enums";
import { useSignedUpNewsletter } from "./hooks";
import { Container, FadeContainer, StyledFindOutMore, StyledSignUpNewsletter, StyledSignUpSuccessMessage } from "./styles";
import { AcquisitionNewsletterPopupTheme, acquisitionNewsletterPopupThemeConfig, ArticleDarkTheme, ArticleTheme, FooterTheme, LiveArticleTheme, NewsletterArticleBottomTheme, newsletterArticleBottomThemeConfig, NewsletterArticleTheme, newsletterArticleThemeConfig, NewsletterListTheme, newsletterListThemeConfig, PostiesTheme, TopicTheme, useAcquisitionNewsletterPopupRender, useArticleRender } from "./variants";
import { HomepageTheme } from "./variants/homepage-theme";
import { useHomepageRender } from "./variants/homepage-theme/hooks";
const defaultThemeConfig = {
  defaultDisplayComponent: DisplayComponent.FindOutMore,
  hasResetAfterSuccess: true,
  hasSanitizeSubscribedNewsletter: true
};
export const NewsletterWidget = ({
  campaignName,
  isToggleSuccessMessage = false,
  multipleNewsletters = false,
  newsletterItems,
  onSuccess,
  variant,
  ...props
}) => {
  var _newsletterHook;
  const renderMap = useMemo(() => ({
    [Variant.AcquisitionNewsletterPopup]: {
      config: acquisitionNewsletterPopupThemeConfig,
      hook: useAcquisitionNewsletterPopupRender,
      renderComponent: AcquisitionNewsletterPopupTheme
    },
    [Variant.Article]: {
      hook: useArticleRender,
      renderComponent: ArticleTheme
    },
    [Variant.ArticleDark]: {
      hook: useArticleRender,
      renderComponent: ArticleDarkTheme
    },
    [Variant.Footer]: {
      renderComponent: FooterTheme
    },
    [Variant.Homepage]: {
      hook: useHomepageRender,
      renderComponent: HomepageTheme
    },
    [Variant.Topic]: {
      renderComponent: TopicTheme
    },
    [Variant.LiveArticle]: {
      renderComponent: LiveArticleTheme
    },
    [Variant.NewsletterList]: {
      config: newsletterListThemeConfig,
      renderComponent: NewsletterListTheme
    },
    [Variant.NewsletterArticle]: {
      config: newsletterArticleThemeConfig,
      renderComponent: NewsletterArticleTheme
    },
    [Variant.NewsletterArticleBottom]: {
      config: newsletterArticleBottomThemeConfig,
      renderComponent: NewsletterArticleBottomTheme
    },
    [Variant.Posties]: {
      renderComponent: PostiesTheme
    }
  }), []);
  const Component = useMemo(() => renderMap[variant].renderComponent, [renderMap, variant]);
  const themeConfig = useMemo(() => {
    var _renderMap$variant$co;
    return (_renderMap$variant$co = renderMap[variant].config) !== null && _renderMap$variant$co !== void 0 ? _renderMap$variant$co : defaultThemeConfig;
  }, [renderMap, variant]);
  const [isResetAfterSuccess, setIsResetAfterSuccess] = useState(false);
  const handleOnSuccess = useCallback(() => {
    setDisplayComponent(DisplayComponent.SignUpSuccessMessage);
    themeConfig.hasResetAfterSuccess && setIsResetAfterSuccess(true);
    onSuccess === null || onSuccess === void 0 || onSuccess();
  }, [onSuccess, themeConfig]);
  const displayComponentRenderFunctionMap = {
    [DisplayComponent.FindOutMore]: p => /*#__PURE__*/React.createElement(StyledFindOutMore, p),
    [DisplayComponent.SignUpNewsletter]: p => /*#__PURE__*/React.createElement(StyledSignUpNewsletter, _extends({
      onSuccess: handleOnSuccess
    }, p)),
    [DisplayComponent.SignUpSuccessMessage]: p => /*#__PURE__*/React.createElement(StyledSignUpSuccessMessage, p)
  };
  useEffect(() => {
    if (!isResetAfterSuccess) return;
    const timer = setTimeout(() => setIsResetAfterSuccess(false), ResetAfterSuccessTimeout);
    return () => clearTimeout(timer);
  }, [isResetAfterSuccess, themeConfig.hasResetAfterSuccess]);
  const newsletterHook = renderMap[variant].hook;
  const hookRenderFunctionMap = (_newsletterHook = newsletterHook === null || newsletterHook === void 0 ? void 0 : newsletterHook(handleOnSuccess)) !== null && _newsletterHook !== void 0 ? _newsletterHook : {};
  const displayComponentMap = Object.entries({
    ...displayComponentRenderFunctionMap,
    ...hookRenderFunctionMap
  }).reduce((accumulator, [key, renderFunction]) => {
    accumulator[key] = renderFunction();
    return accumulator;
  }, {});
  const signedUpNewsletterItemIds = useSignedUpNewsletter();
  const sanitizedNewsletterItems = useDeepCompareMemo(() => {
    if (!themeConfig.hasSanitizeSubscribedNewsletter) return newsletterItems;
    return newsletterItems.filter(newsletter => !signedUpNewsletterItemIds.includes(newsletter.entityId));
  }, [newsletterItems, signedUpNewsletterItemIds, themeConfig.hasSanitizeSubscribedNewsletter]);
  const [displayComponent, setDisplayComponent] = useState(themeConfig.defaultDisplayComponent);
  const resetDisplayComponent = useCallback(() => {
    setDisplayComponent(sanitizedNewsletterItems.length > 0 ? DisplayComponent.SignUpNewsletter : themeConfig.defaultDisplayComponent);
  }, [sanitizedNewsletterItems, themeConfig.defaultDisplayComponent]);
  const latestResetDisplayComponent = useLatest(resetDisplayComponent);
  useEffect(() => {
    if (isResetAfterSuccess) return;
    latestResetDisplayComponent.current();
  }, [isResetAfterSuccess, latestResetDisplayComponent]);
  useEffect(() => {
    if (isToggleSuccessMessage) {
      setDisplayComponent(DisplayComponent.SignUpSuccessMessage);
    } else {
      latestResetDisplayComponent.current();
    }
  }, [isToggleSuccessMessage, latestResetDisplayComponent]);

  // Get context value from props
  const {
    dataMatrix,
    findOutMoreButtonDisplayComponent,
    queryParametersForSocialButtons,
    signUpButtonDisplayComponent,
    titleComponent
  } = props;
  return /*#__PURE__*/React.createElement(NewsletterContextProvider, {
    multipleNewsletters: multipleNewsletters,
    value: {
      campaignName,
      dataMatrix,
      findOutMoreButtonDisplayComponent,
      newsletterItems: sanitizedNewsletterItems,
      queryParametersForSocialButtons,
      signUpButtonDisplayComponent,
      titleComponent,
      variant
    },
    "data-qa": "newsletter-widget"
  }, /*#__PURE__*/React.createElement(Component, props, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(SwitchTransition, {
    mode: "out-in"
  }, /*#__PURE__*/React.createElement(Fade, {
    appear: false,
    key: displayComponent,
    timeout: 500
  }, /*#__PURE__*/React.createElement(FadeContainer, null, Object.entries(displayComponentMap).map(([key, component]) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: key
  }, key === displayComponent && component))))))));
};
NewsletterWidget.displayName = "NewsletterWidget";